/**
 * Beer colors for Facility View vessels.
 *
 * Order: product.color (hex set on the product screen) → SRM estimated from the
 * product style/name → deterministic per-product fallback from the SRM range
 * (so every beer gets a stable, realistic color today).
 */

/** Standard SRM → sRGB reference (SRM 1..40). */
const SRM_HEX: string[] = [
  '#FFE699', '#FFD878', '#FFCA5A', '#FFBF42', '#FBB123', '#F8A600', '#F39C00', '#EA8F00',
  '#E58500', '#DE7C00', '#D77200', '#CF6900', '#CB6200', '#C35900', '#BB5100', '#B54C00',
  '#B04500', '#A63E00', '#A13700', '#9B3200', '#952D00', '#8E2900', '#882300', '#821E00',
  '#7B1A00', '#771900', '#701400', '#6A0E00', '#660D00', '#5E0B00', '#5A0A02', '#600903',
  '#520907', '#4C0505', '#470606', '#440607', '#3F0708', '#3B0607', '#3A070B', '#36080A',
];

export function srmToHex(srm: number): string {
  const n = Math.max(1, Math.min(40, Math.round(Number(srm) || 1)));
  return SRM_HEX[n - 1];
}

/** Rough SRM by style keyword (checked against style + product name). */
const STYLE_SRM: [RegExp, number][] = [
  [/imperial stout|russian/i, 40],
  [/stout/i, 38],
  [/porter|schwarz|black/i, 32],
  [/brown|dunkel|doppelbock|bock/i, 20],
  [/red|amber|irish|scotch|altbier/i, 14],
  [/m[aä]rzen|oktoberfest|oktobear|vienna|festbier/i, 9],
  [/hazy|neipa|new england|juicy/i, 5],
  [/ipa|india pale|pale ale|extra pale|apa/i, 6],
  [/tripel|saison|belgian|farmhouse|golden/i, 5],
  [/wheat|hefe|witbier|wit\b/i, 4],
  [/sour|gose|berliner|kettle/i, 4],
  [/pilsner|pils|helles|light|blonde|lager|k[oö]lsch|cream ale/i, 3],
];

export function srmFromStyle(...texts: (string | null | undefined)[]): number | null {
  const hay = texts.filter(Boolean).join(' ');
  if (!hay) return null;
  for (const [re, srm] of STYLE_SRM) {
    if (re.test(hay)) return srm;
  }
  return null;
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Deterministic fallback: a realistic beer SRM (3..30) chosen from the product key. */
export function fallbackSrm(key: string | number | null | undefined): number {
  const k = String(key ?? '');
  if (!k) return 8;
  return 3 + (hashString(k) % 28);
}

const HEX_RE = /^#[0-9a-f]{6}$/i;

export interface BeerColorInput {
  productId?: number | string | null;
  productName?: string | null;
  productStyle?: string | null;
  productColor?: string | null;
}

export interface BeerColor {
  fill: string;
  text: string;
  source: 'product' | 'style' | 'fallback';
}

/** WCAG relative luminance of a #RRGGBB color. */
export function luminance(hex: string): number {
  const m = HEX_RE.test(hex) ? hex : '#888888';
  const c = [1, 3, 5].map((i) => parseInt(m.slice(i, i + 2), 16) / 255);
  const lin = c.map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Black or white, whichever reads better on the fill. */
export function readableTextColor(fill: string): '#111111' | '#FFFFFF' {
  return contrast(fill, '#111111') >= contrast(fill, '#FFFFFF') ? '#111111' : '#FFFFFF';
}

export function beerColor(input: BeerColorInput): BeerColor {
  let fill: string;
  let source: BeerColor['source'];
  if (input.productColor && HEX_RE.test(input.productColor)) {
    fill = input.productColor.toUpperCase();
    source = 'product';
  } else {
    const srm = srmFromStyle(input.productStyle, input.productName);
    if (srm != null) {
      fill = srmToHex(srm);
      source = 'style';
    } else {
      fill = srmToHex(fallbackSrm(input.productId ?? input.productName));
      source = 'fallback';
    }
  }
  return { fill, text: readableTextColor(fill), source };
}

/** Neutral look for empty vessels. */
export const EMPTY_VESSEL_FILL = '#E6E9ED';
export const EMPTY_VESSEL_TEXT = '#333333';
